import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiAlert from "components/SuiAlert";
import { CircularProgress } from "@mui/material";
import ProcesosApi from "api/procesosApi";
import { styles } from "../styles";

const normalizarTexto = (texto, tipo = "lower") => {
  if (!texto) return "";
  const base = texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ñ/g, "n")
    .replace(/Ñ/g, "N")
    .replace(/\s+/g, "_");
  return tipo === "upper" ? base.toUpperCase() : base.toLowerCase();
};

export const validateAnexos = async (procesoId) => {
  try {
    // 1. Obtener datos de la proceso
    const procesoResponse = await ProcesosApi.fetchProcesoById(procesoId);
    const proceso = procesoResponse.data;
    let validaciones = [];
    // 2. Verificar Cédula del Cliente
    const validacionCedula = await ProcesosApi.verificarAnexoProceso(procesoId, {
      tipo_anexo: "CEDULA_CLIENTE",
    });
    validaciones.push({
      tipo: "Cédula del Cliente",
      presente: validacionCedula.data.exists,
      requerido: true,
    });
    // 3. Verificar Firma del Cliente
    const validacionFirma = await ProcesosApi.verificarAnexoProceso(procesoId, {
      tipo_anexo: "FIRMA_CLIENTE",
    });
    validaciones.push({
      tipo: "Firma del Cliente",
      presente: validacionFirma.data.exists,
      requerido: true,
    });
    // 4. Verificar si Posee Bienes y procesar documentos de bienes
    if (proceso.posee_bienes) {
      try {
        const bienesResponse = await ProcesosApi.obtenerBienesProceso(procesoId);
        // Asegurarnos de que bienesResponse.data sea un array
        const bienes = Array.isArray(bienesResponse.data)
          ? bienesResponse.data
          : bienesResponse.data?.bienes
          ? bienesResponse.data.bienes
          : [];
        // Validar cada bien
        for (const bien of bienes) {
          if (bien.tipo_bien === "INMUEBLE") {
            // Construir el tipo_anexo dinámicamente incluyendo la matrícula inmobiliaria
            let nomb = normalizarTexto(bien.matricula_inmobiliaria, "upper");
            const tipoAnexo = `CERTIFICADO_TRADICION_${nomb}`;
            // Realizar la proceso de verificación usando el tipo_anexo construido
            const validacionTradicion = await ProcesosApi.verificarAnexoProceso(procesoId, {
              tipo_anexo: tipoAnexo,
            });
            // Registrar la validación
            validaciones.push({
              tipo: "Certificado de Tradición y Libertad",
              presente: validacionTradicion.data.exists,
              requerido: true,
              detalle: `Matrícula: ${bien.matricula_inmobiliaria || "No especificada"}`,
              bien_id: bien.id,
            });
          } else if (bien.tipo_bien === "MUEBLE") {
            // Construir el tipo_anexo dinámico incluyendo la placa
            let nomb = normalizarTexto(bien.placa, "upper");
            const tipoAnexo = `TARJETA_PROPIEDAD_${nomb}`;
            // Realizar la proceso de verificación usando el tipo_anexo construido
            const validacionPropiedad = await ProcesosApi.verificarAnexoProceso(procesoId, {
              tipo_anexo: tipoAnexo,
            });
            // Registrar la validación
            validaciones.push({
              tipo: "Tarjeta de Propiedad",
              presente: validacionPropiedad.data.exists,
              requerido: true,
              detalle: `Placa: ${bien.placa || "No especificada"}`,
              bien_id: bien.id,
            });
          }
        }
      } catch (bienesError) {
        console.error("Error procesando Bienes del Proceso:", bienesError);
        validaciones.push({
          tipo: "Documentos de Bienes",
          presente: false,
          requerido: true,
          error: "Error al verificar documentos de los Bienes del Proceso",
        });
      }
    }
    // 5. Si es empleado
    // 5.1. Verificar la Carta Laboral y el Sertificado de Ingresos
    if (proceso.cliente?.tipo_ocupacion === "EMPLEADO(A)") {
      //Carta Laboral: CARTA_LABORAL
      const validacionCartaLaboral = await ProcesosApi.verificarAnexoProceso(procesoId, {
        tipo_anexo: "CARTA_LABORAL",
      });
      validaciones.push({
        tipo: "Carta Laboral",
        presente: validacionCartaLaboral.data.exists,
        requerido: true,
      });
      //Certificado de Ingresos: CERTIFICACION_INGRESOS
      // const validacionIngresos = await ProcesosApi.verificarAnexoProceso(procesoId, {
      //   tipo_anexo: "CERTIFICACION_INGRESOS",
      // });
      // validaciones.push({
      //   tipo: "Certificado de Ingresos",
      //   presente: validacionIngresos.data.exists,
      //   requerido: true,
      // });
    }
    // 6. Calcular resultado final
    const documentosFaltantes = validaciones.filter(
      (validacion) => validacion.requerido && !validacion.presente
    );
    const completed = documentosFaltantes.length === 0;
    return {
      completed: completed,
      error: completed ? false : true,
      results: {
        validaciones: validaciones,
        documentosFaltantes: documentosFaltantes,
        errorMessage: documentosFaltantes.length
          ? `Faltan ${documentosFaltantes.length} documento${
              documentosFaltantes.length === 1 ? "" : "s"
            } requerido${documentosFaltantes.length === 1 ? "" : "s"}`
          : "Todos los documentos requeridos están presentes",
      },
    };
  } catch (error) {
    console.error("Error al validar los Anexos del proceso:", error);
    return {
      completed: false,
      error: true,
      results: {
        validaciones: [],
        documentosFaltantes: [],
        errorMessage: "Error al validar los Anexos del Proceso",
        error: error.message,
      },
    };
  }
};

const VerificacionAnexos = ({
  procesoId,
  onStepValidation = null,
  showResults = false,
  initialData = null,
}) => {
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  const [analisis, setAnalisis] = useState(
    initialData || {
      completed: false,
      error: false,
      results: {
        validaciones: [],
        documentosFaltantes: [],
        errorMessage: "",
      },
    }
  );

  useEffect(() => {
    const validar = async () => {
      if (initialData) {
        setAnalisis(initialData);
        if (onStepValidation) {
          onStepValidation(initialData.completed);
        }
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const resultado = await validateAnexos(procesoId);
        setAnalisis(resultado.results);
        if (onStepValidation) {
          onStepValidation(resultado.completed);
        }
      } catch (error) {
        console.error("Error al validar los Anexos del Proceso:", error);
        setError(obtenerMensajeError(error, "Error al validar los Anexos del Proceso"));
        if (onStepValidation) {
          onStepValidation(false, "Error al validar los anexos");
        }
      } finally {
        setLoading(false);
      }
    };
    validar();
  }, [procesoId, onStepValidation, initialData]);

  if (loading) {
    return (
      <SuiBox sx={styles.loadingContainer}>
        <CircularProgress />
      </SuiBox>
    );
  }

  if (error) {
    return (
      <SuiBox sx={styles.errorContainer}>
        <SuiAlert color="error">{error}</SuiAlert>
      </SuiBox>
    );
  }

  if (!showResults) {
    return null;
  }

  return (
    <Box sx={styles.root}>
      <SuiBox mb={3}>
        <SuiAlert
          color={analisis.documentosFaltantes.length > 0 ? "warning" : "success"}
          sx={{ marginBottom: 2 }}
        >
          {analisis.mensaje}
        </SuiAlert>

        {analisis.validaciones.map((validacion, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: 2,
              marginBottom: 1,
              backgroundColor: validacion.presente ? "success.light" : "error.light",
              borderRadius: 1,
            }}
          >
            <Box>
              <SuiTypography variant="button" fontWeight="medium">
                {validacion.tipo}
              </SuiTypography>
              {validacion.detalle && (
                <SuiTypography variant="caption" display="block">
                  {validacion.detalle}
                </SuiTypography>
              )}
            </Box>
            <SuiTypography
              variant="button"
              fontWeight="medium"
              color={validacion.presente ? "success" : "error"}
            >
              {validacion.presente ? "Presente" : "Faltante"}
            </SuiTypography>
          </Box>
        ))}
      </SuiBox>
    </Box>
  );
};

VerificacionAnexos.propTypes = {
  procesoId: PropTypes.number.isRequired,
  onStepValidation: PropTypes.func,
  showResults: PropTypes.bool,
  initialData: PropTypes.object,
};

export default VerificacionAnexos;
